import { createLazyContext } from "@redotech/react-util/context";
import {
  RedoDiscount,
  RedoDraftDiscount,
} from "@redotech/redo-discounts-common/discount/discount-db-parser";
import { getShopifySegments } from "@redotech/redo-merchant-marketing-rpc/schema/segments/get-shopify-segments";
import { Provider } from "@redotech/redo-model/order";
import { ProductInfo } from "@redotech/redo-model/product";
import { createContext } from "react";
import type * as z from "zod";
import { TimeOfDay } from "../arbiter-components/time/redo-time-picker";
import { ModalProduct } from "../product/product-selection-modal";
export { ShopifyBasicDiscountCustomerEligibility } from "@redotech/cockroachdb-codegen/congodb/shopify-basic-discount-customer-eligibility";

export type ShopifySegment = z.infer<typeof getShopifySegments.output>[number];
export interface ProductSelection {
  id: string;
  title: string;
}

export const INITIAL_CODE_NAME = "DISCOUNT";
export const INITIAL_START_TIME = { hours: 0, minutes: 0 } as const;
export const INITIAL_END_TIME = { hours: 23, minutes: 59 } as const;

export function getInitialSegments(
  existingDiscount: RedoDiscount | undefined,
  shopifySegments: ShopifySegment[] | undefined,
): ShopifySegment[] {
  if (existingDiscount?.discountSettings.limitToSegments && shopifySegments) {
    return shopifySegments.filter((segment) =>
      existingDiscount.discountSettings.limitToSegments?.includes(segment.id),
    );
  }
  return [];
}

export function combineDateTime(
  date: Date | undefined,
  time: TimeOfDay | null,
): Date | undefined {
  if (!date) {
    return undefined;
  }
  const combined = new Date(date);

  if (time) {
    combined.setHours(time.hours, time.minutes, 0, 0);
  } else {
    combined.setHours(0, 0, 0, 0);
  }

  return combined;
}

export interface DiscountMethodsContextI {
  deleteDiscount: (id: string) => Promise<void>;
  createDiscount: (args: {
    discountConfiguration: RedoDraftDiscount;
  }) => Promise<string>;
  updateDiscount: (args: {
    discountId: string;
    discountConfiguration: RedoDraftDiscount;
  }) => Promise<void>;
  searchProducts?: (params: {
    search: string;
    signal: AbortSignal;
  }) => Promise<ModalProduct[]>;
  searchCustomers?: (
    searchText: string,
    signal: AbortSignal,
  ) => Promise<
    {
      shopifyCustomerId?: string | null | undefined;
      email: string | null | undefined;
      first_name?: string | null;
      last_name?: string | null;
    }[]
  >;
}

const EmptyDiscountMethodsContext: DiscountMethodsContextI = {
  deleteDiscount: async () => {},
  createDiscount: async () => "",
  updateDiscount: async () => {},
};

export const CollectionsContext =
  createLazyContext<{ id: string; name: string }[]>();
export const DiscountsContext = createLazyContext<RedoDiscount[]>();
export const ProductsContext =
  createLazyContext<{ id: string; name: string }[]>();

export const DiscountMethodsContext = createContext<DiscountMethodsContextI>(
  EmptyDiscountMethodsContext,
);

export const ShopifySegmentsContext = createLazyContext<ShopifySegment[]>();

/**
 * Ecommerce providers available to the team; empty defaults
 * to Shopify. If we eventually support teams with both Shopify
 * and BigCommerce, we'll need to update this.
 */
export const AvailableProvidersContext = createContext<Provider[]>([]);

export function convertProductInfoToModalProduct(
  product: ProductInfo,
): ModalProduct {
  return {
    id: product.id,
    title: product.name,
    description: product.description || "",
    handle: "",
    image: {
      src: product.image?.url || null,
      altText: product.image?.alt || null,
    },
    images: product.image?.url
      ? [{ src: product.image.url, altText: product.image.alt || null }]
      : [],
    tags: product.tags || [],
    variants: product.variants.map((variant) => ({
      admin_graphql_api_id: variant.id,
      inventory_quantity: variant.quantityAvailable || 0,
      title: variant.name,
      price: variant.price.toString(),
      image: {
        src: variant.image?.url || null,
        altText: variant.image?.alt || null,
      },
      productTitle: product.name,
    })),
  };
}
